import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import ObjectSelect, { type ObjectSelectValue } from "@components/ObjectSelect";
import type { ContractMeterLink } from "@features/contracts/api";
import {
  type ChannelMeterRow,
  type DeviceObject,
  useChannelsFlat,
  useObjects,
} from "@features/devices/api";
import EnterpriseTable from "@shared/ui/enterprise-table";
import { Button, Space, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { memo, useCallback, useMemo, useState } from "react";

const meterKey = (r: ChannelMeterRow | ContractMeterLink) =>
  `${r.region_id}:${r.object_id}:${r.feeder_id}:${r.meter_number}`;

interface ContractMeterTabProps {
  meters: ContractMeterLink[];
  onMetersChange: (meters: ContractMeterLink[]) => void;
  onDirty: () => void;
}

const ContractMeterTab = memo(function ContractMeterTab({
  meters,
  onMetersChange,
  onDirty,
}: ContractMeterTabProps) {
  const objectsQuery = useObjects();
  const objects = objectsQuery.data ?? [];

  const [meterObject, setMeterObject] = useState<ObjectSelectValue>();
  const channelsQuery = useChannelsFlat(meterObject?.objectId);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);

  const objectsMap = useMemo(() => {
    const map = new Map<string, DeviceObject>();
    for (const obj of objects) map.set(`${obj.syb_rnk}:${obj.n_ob}`, obj);
    return map;
  }, [objects]);

  const availableMeters = useMemo(() => {
    if (!channelsQuery.data) return [];
    const linked = new Set(meters.map(meterKey));
    return channelsQuery.data.filter((ch) => !linked.has(meterKey(ch)));
  }, [channelsQuery.data, meters]);

  const uniqueObjectIds = useMemo(() => {
    const ids = new Set(meters.map((m) => m.object_id));
    return [...ids];
  }, [meters]);
  const linkedObjectId = uniqueObjectIds.length === 1 ? uniqueObjectIds[0] : undefined;
  const linkedChannelsQuery = useChannelsFlat(linkedObjectId);
  const linkedMeters = useMemo(() => {
    if (!meters.length) return [];
    if (!linkedChannelsQuery.data) return [];
    const linked = new Set(meters.map(meterKey));
    return linkedChannelsQuery.data.filter((ch) => linked.has(meterKey(ch)));
  }, [meters, linkedChannelsQuery.data]);

  const addSelectedMeters = useCallback(() => {
    if (!channelsQuery.data) return;
    const keysSet = new Set(selectedKeys.map(String));
    const toAdd: ContractMeterLink[] = channelsQuery.data
      .filter((ch) => keysSet.has(meterKey(ch)))
      .map((ch) => ({
        region_id: ch.region_id,
        object_id: ch.object_id,
        feeder_id: ch.feeder_id,
        meter_number: String(ch.meter_number),
      }));
    onMetersChange([...meters, ...toAdd]);
    setSelectedKeys([]);
    onDirty();
  }, [channelsQuery.data, selectedKeys, meters, onMetersChange, onDirty]);

  const removeMeters = useCallback(
    (keys: React.Key[]) => {
      const removeSet = new Set(keys.map(String));
      onMetersChange(meters.filter((m) => !removeSet.has(meterKey(m))));
      onDirty();
    },
    [meters, onMetersChange, onDirty],
  );

  const objectColumn = {
    title: "Объект",
    width: 160,
    ellipsis: { showTitle: false },
    render: (_: unknown, row: ChannelMeterRow) => {
      const obj = objectsMap.get(`${row.region_id}:${row.object_id}`);
      const name = obj?.txt ?? `#${row.object_id}`;
      return <Tooltip title={name}>{name}</Tooltip>;
    },
  };

  const meterColumns: ColumnsType<ChannelMeterRow> = [
    objectColumn,
    { title: "Счётчик №", dataIndex: "meter_number", width: 120 },
    {
      title: "Фидер",
      dataIndex: "feeder_name",
      ellipsis: { showTitle: false },
      render: (v?: string) => <Tooltip title={v}>{v ?? "—"}</Tooltip>,
    },
    { title: "Код ТУ", dataIndex: "cod_kre", width: 140, render: (v?: string) => v || "—" },
  ];

  const linkedMeterColumns: ColumnsType<ChannelMeterRow> = [
    ...meterColumns,
    {
      title: "",
      width: 50,
      render: (_: unknown, record: ChannelMeterRow) => (
        <Tooltip title="Убрать">
          <Button
            size="small"
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeMeters([meterKey(record)])}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <Space orientation="vertical" size="large" style={{ width: "100%" }}>
      <EnterpriseTable<ChannelMeterRow>
        rowKey={(r) => meterKey(r)}
        columns={linkedMeterColumns}
        dataSource={linkedMeters}
        loading={linkedChannelsQuery.isLoading && meters.length > 0}
        size="small"
        scroll={{ x: 400 }}
        pagination={false}
      />
      <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
        <ObjectSelect
          objects={objects}
          loading={objectsQuery.isLoading}
          value={meterObject}
          onChange={(value) => {
            setMeterObject(value);
            setSelectedKeys([]);
          }}
          allowClear
          placeholder="Выберите объект для добавления счётчиков"
          style={{ width: "100%" }}
        />
        {meterObject && (
          <EnterpriseTable<ChannelMeterRow>
            rowKey={(r) => meterKey(r)}
            columns={meterColumns}
            dataSource={availableMeters}
            loading={channelsQuery.isLoading}
            size="small"
            scroll={{ x: 400 }}
            pagination={false}
            rowSelection={{
              selectedRowKeys: selectedKeys,
              onChange: setSelectedKeys,
            }}
            toolbar={{
              bulkActions:
                selectedKeys.length > 0 ? (
                  <Button
                    type="primary"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={addSelectedMeters}
                  >
                    Добавить
                  </Button>
                ) : undefined,
              selectedCount: selectedKeys.length,
            }}
          />
        )}
      </Space>
    </Space>
  );
});

export default ContractMeterTab;
